import { ApolloError } from '@apollo/client';
import { Stack } from '@mui/material';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  LinearProgress,
  Button,
  DownloadIcon,
  SearchForm,
  SearchFormProps,
  CircularProgress,
} from '@occc/ui-design-system';
import { IPaginationProps, LoadedContent, Pagination } from '@occc/ui-design-system';

import { TobaccoStoreSubmissionsTableRow } from './tobacco-stores-submissions-table-row.component';

import { formatSubmittedAt } from '../tobacco-submissions.utils';
import { RetailerNetworkScanDataSiteSubmission } from '../tobacco-submissions.types';

export type TobaccoStoresSubmissionsTableProps = {
  data?: RetailerNetworkScanDataSiteSubmission[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  submittedAt: Date;
  isDownloading?: boolean;
  onDownloadCsvClick: () => void;
  onSearchSubmit: SearchFormProps['onSubmit'];
};

export const TobaccoStoresSubmissionsTable = ({
  submittedAt,
  data,
  loading,
  error,
  paginationProps,
  isDownloading,
  onDownloadCsvClick,
  onSearchSubmit,
}: TobaccoStoresSubmissionsTableProps) => {
  const renderData = (items: RetailerNetworkScanDataSiteSubmission[]) => (
    <>
      {loading && <LinearProgress />}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Site ID</TableCell>
            <TableCell>Site Name (DBA)</TableCell>
            <TableCell>Altria</TableCell>
            <TableCell>RJR</TableCell>
            <TableCell>MSA</TableCell>
            <TableCell>Multi-Unit Tran</TableCell>
            <TableCell>Loyalty Tran</TableCell>
            <TableCell align='right'>Est. Reimbursement</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TobaccoStoreSubmissionsTableRow key={item.siteId} item={item} />
          ))}
        </TableBody>
      </Table>
      <Pagination {...paginationProps} />
    </>
  );

  return (
    <TableContainer sx={{ overflowY: 'auto' }}>
      <TableHeader
        title={<>Stores Submitted &nbsp; | &nbsp; {formatSubmittedAt(submittedAt)}</>}
        actions={
          <Stack direction='row' alignItems='center' gap={1}>
            <SearchForm onSubmit={onSearchSubmit} />
            <Button
              variant='contained'
              color='secondary'
              onClick={onDownloadCsvClick}
              disabled={isDownloading}
              startIcon={
                isDownloading ? <CircularProgress size={22} thickness={4} /> : <DownloadIcon />
              }
            >
              Download CSV
            </Button>
          </Stack>
        }
      />
      <LoadedContent error={error} loading={loading} items={data} renderData={renderData} />
    </TableContainer>
  );
};
